// src/components/BookCover/usePdfGenerator.js
import { useCallback } from 'react';
import { jsPDF } from 'jspdf';
import { API_BASE } from '../../utils/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('rv_access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const A4_W  = 595;
const A4_H  = 842;
const MARGIN = 32;

const FACE_LABELS  = { front: 'Front Cover', back: 'Back Cover', spine: 'Spine' };
const SYSTEM_NAMES = new Set(['bg-front', 'bg-spine', 'bg-back', 'isbn-barcode', 'margin-guide']);

// ── Image helpers ─────────────────────────────────────────────────────────────

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function generateSpotlightImage(faceDataUrl, bounds, faceW, faceH) {
  const scale    = 2;
  const offscreen = document.createElement('canvas');
  offscreen.width  = faceW * scale;
  offscreen.height = faceH * scale;
  const ctx = offscreen.getContext('2d');
  const img = await loadImage(faceDataUrl);

  ctx.drawImage(img, 0, 0, offscreen.width, offscreen.height);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.fillRect(0, 0, offscreen.width, offscreen.height);

  const pad = 6 * scale;
  const ex  = Math.max(0, bounds.left  * scale - pad);
  const ey  = Math.max(0, bounds.top   * scale - pad);
  const ew  = Math.min(offscreen.width  - ex, bounds.width  * scale + pad * 2);
  const eh  = Math.min(offscreen.height - ey, bounds.height * scale + pad * 2);

  ctx.save();
  ctx.beginPath();
  ctx.rect(ex, ey, ew, eh);
  ctx.clip();
  ctx.drawImage(img, 0, 0, offscreen.width, offscreen.height);
  ctx.restore();

  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth   = 3 * scale;
  ctx.strokeRect(ex, ey, ew, eh);

  return offscreen.toDataURL('image/png');
}

// ── Canvas helpers ────────────────────────────────────────────────────────────

function getFaceForObject(obj, faceW, spineW) {
  const w  = (obj.width  || 0) * (obj.scaleX || 1);
  const cx = (obj.left   || 0) + w / 2;
  if (cx < faceW)          return { face: 'back',  faceOffsetX: 0,             faceWidth: faceW  };
  if (cx < faceW + spineW) return { face: 'spine', faceOffsetX: faceW,         faceWidth: spineW };
  return                          { face: 'front', faceOffsetX: faceW + spineW, faceWidth: faceW  };
}

function getObjectLabel(obj, faceDims, peers) {
  if (obj._label) return obj._label;
  const base = obj.name || obj.text?.slice(0, 24) || obj.type || 'Element';
  const { faceW, spineW } = faceDims;
  const cx = (obj.left || 0) + (obj.width || 0) / 2;
  const faceHint = cx < faceW ? 'back' : cx < faceW + spineW ? 'spine' : 'front';
  const auto = `${base} · ${faceHint}`;
  if (!peers || peers.length <= 1) return auto;
  const dupes = peers.filter(o => {
    if (o._label) return false;
    const b2 = o.name || o.text?.slice(0, 24) || o.type || 'Element';
    const c2 = (o.left || 0) + (o.width || 0) / 2;
    const f2 = c2 < faceW ? 'back' : c2 < faceW + spineW ? 'spine' : 'front';
    return `${b2} · ${f2}` === auto;
  });
  if (dupes.length <= 1) return auto;
  const idx = dupes.indexOf(obj);
  return idx <= 0 ? auto : `${auto} (${idx + 1})`;
}

// ── jsPDF helpers ─────────────────────────────────────────────────────────────

function wrapText(pdf, text, maxWidth) {
  return pdf.splitTextToSize(String(text || ''), maxWidth);
}

function drawTableRow(pdf, row, y, colX, colW, rowH, isHeader, isAlt) {
  const totalW = colW.reduce((a, b) => a + b, 0);
  if (isHeader) {
    pdf.setFillColor(35, 35, 55);
    pdf.rect(colX[0], y, totalW, rowH, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
  } else {
    if (isAlt) {
      pdf.setFillColor(245, 245, 250);
      pdf.rect(colX[0], y, totalW, rowH, 'F');
    }
    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(7.5);
    pdf.setFont('helvetica', 'normal');
  }
  row.forEach((cell, i) => {
    pdf.text(wrapText(pdf, cell, colW[i] - 8), colX[i] + 4, y + 10, { maxWidth: colW[i] - 8 });
  });
  pdf.setDrawColor(200, 200, 210);
  pdf.setLineWidth(0.3);
  pdf.line(colX[0], y + rowH, colX[0] + totalW, y + rowH);
  return y + rowH;
}

function addDashedFoldLines(pdf, imgX, imgY, imgW, imgH, faceW, spineW, totalW) {
  const spineStart = imgX + (faceW / totalW) * imgW;
  const spineEnd   = imgX + ((faceW + spineW) / totalW) * imgW;
  pdf.setDrawColor(220, 60, 60);
  pdf.setLineWidth(0.6);
  if (pdf.setLineDashPattern) pdf.setLineDashPattern([4, 3], 0);
  pdf.line(spineStart, imgY, spineStart, imgY + imgH);
  pdf.line(spineEnd,   imgY, spineEnd,   imgY + imgH);
  if (pdf.setLineDashPattern) pdf.setLineDashPattern([], 0);
}

// ── Per-page PDF renderers ────────────────────────────────────────────────────

function renderCoverPage(pdf, page, versionName) {
  const LW = A4_H, LH = A4_W, pad = 36;
  const aspect = page.totalW / page.faceH;
  let imgW = LW - pad * 2, imgH = imgW / aspect;
  if (imgH > LH - pad * 2 - 20) { imgH = LH - pad * 2 - 20; imgW = imgH * aspect; }
  const imgX = (LW - imgW) / 2, imgY = pad + 20;
  pdf.setFillColor(18, 18, 28); pdf.rect(0, 0, LW, LH, 'F');
  pdf.setFontSize(11); pdf.setTextColor(200, 200, 220); pdf.setFont('helvetica', 'bold');
  pdf.text('BOOK COVER — PRINT SPECIFICATION', LW / 2, pad, { align: 'center' });
  pdf.addImage(page.imageUrl, 'PNG', imgX, imgY, imgW, imgH);
  addDashedFoldLines(pdf, imgX, imgY, imgW, imgH, page.faceW, page.spineW, page.totalW);
  pdf.setFontSize(7); pdf.setTextColor(120, 120, 140); pdf.setFont('helvetica', 'normal');
  pdf.text(`${versionName || 'Cover Design'} · Generated ${new Date().toLocaleDateString()}`, LW / 2, LH - 14, { align: 'center' });
}

function renderTablePage(pdf, page) {
  const PW = A4_W, PH = A4_H;
  pdf.setFillColor(35, 35, 55); pdf.rect(0, 0, PW, 36, 'F');
  pdf.setFontSize(10); pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold');
  pdf.text('PRINT SPECIFICATIONS SUMMARY', PW / 2, 22, { align: 'center' });
  const tableX = MARGIN, tableW = PW - MARGIN * 2;
  const colW = [Math.round(tableW * 0.30), Math.round(tableW * 0.15), Math.round(tableW * 0.10), Math.round(tableW * 0.45)];
  const colX = colW.reduce((acc, w, i) => { acc.push(i === 0 ? tableX : acc[i-1] + colW[i-1]); return acc; }, []);
  const rowH = 24;
  let y = 44;
  y = drawTableRow(pdf, ['Element', 'Face', 'Ref ID', 'Specifications'], y, colX, colW, rowH, true, false);
  if (!page.rows.length) {
    drawTableRow(pdf, ['All elements', '—', '—', 'Normal — no special specifications'], y, colX, colW, rowH, false, false);
  } else {
    page.rows.forEach((row, i) => {
      if (y + rowH > PH - MARGIN) {
        pdf.addPage('a4', 'portrait'); y = MARGIN;
        y = drawTableRow(pdf, ['Element', 'Face', 'Ref ID', 'Specifications'], y, colX, colW, rowH, true, false);
      }
      y = drawTableRow(pdf, [row.label, row.faceLabel, row.elementId, row.instructions || 'Normal'], y, colX, colW, rowH, false, i % 2 === 1);
    });
    if (y + rowH <= PH - MARGIN) {
      drawTableRow(pdf, ['All other elements', '—', '—', 'Normal'], y, colX, colW, rowH, false, page.rows.length % 2 === 1);
    }
  }
  pdf.setFontSize(7); pdf.setTextColor(160, 160, 180); pdf.setFont('helvetica', 'normal');
  pdf.text(`ReVisionari · Generated ${new Date().toLocaleDateString()}`, PW / 2, PH - 14, { align: 'center' });
}

function renderElementPage(pdf, page) {
  const PW = A4_W, PH = A4_H;
  pdf.setFillColor(35, 35, 55); pdf.rect(0, 0, PW, 36, 'F');
  pdf.setFontSize(9); pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold');
  pdf.text(page.elementLabel, MARGIN, 22);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(160, 160, 200);
  pdf.text(page.faceLabel, PW - MARGIN, 22, { align: 'right' });
  const bodyY = 44, bodyH = PH - bodyY - MARGIN;
  const leftW = Math.round(PW * 0.54), rightW = PW - leftW, rightX = leftW;
  if (page.spotlightUrl) {
    const aspect = page.faceWidth / page.faceH;
    let imgW = leftW - MARGIN, imgH = imgW / aspect;
    if (imgH > bodyH - 8) { imgH = bodyH - 8; imgW = imgH * aspect; }
    const imgX = MARGIN / 2 + (leftW - MARGIN - imgW) / 2;
    const imgY = bodyY + (bodyH - imgH) / 2;
    pdf.addImage(page.spotlightUrl, 'PNG', imgX, imgY, imgW, imgH);
  }
  pdf.setDrawColor(210, 210, 225); pdf.setLineWidth(0.5);
  pdf.line(leftW, bodyY, leftW, PH - MARGIN);
  let sy = bodyY + 16;
  const sx = rightX + 16, sw = rightW - 24;
  pdf.setFontSize(8); pdf.setTextColor(100, 100, 130); pdf.setFont('helvetica', 'bold');
  pdf.text('PRINT SPECIFICATIONS', sx, sy); sy += 4;
  pdf.setDrawColor(100, 100, 180); pdf.setLineWidth(1);
  pdf.line(sx, sy, sx + sw, sy); sy += 14;
  const field = (label, value) => {
    pdf.setFontSize(7.5); pdf.setTextColor(130, 130, 160); pdf.setFont('helvetica', 'bold');
    pdf.text(label, sx, sy); sy += 11;
    pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30);
    const lines = wrapText(pdf, value || '—', sw);
    pdf.text(lines, sx, sy); sy += lines.length * 10 + 6;
  };
  field('Element', page.elementLabel);
  field('Face', page.faceLabel);
  field('Ref ID', page.elementId);
  sy += 4; pdf.setDrawColor(210, 210, 225); pdf.setLineWidth(0.4);
  pdf.line(sx, sy, sx + sw, sy); sy += 12;
  pdf.setFontSize(7.5); pdf.setTextColor(100, 100, 130); pdf.setFont('helvetica', 'bold');
  pdf.text('INSTRUCTIONS', sx, sy); sy += 12;
  if (!page.comments?.length) {
    pdf.setFont('helvetica', 'italic'); pdf.setTextColor(160, 160, 160); pdf.setFontSize(7.5);
    pdf.text('No specifications set.', sx, sy);
  } else {
    page.comments.forEach(c => {
      if (sy > PH - MARGIN - 20) return;
      pdf.setFontSize(7); pdf.setTextColor(100, 100, 180); pdf.setFont('helvetica', 'bold');
      pdf.text(c.userName || 'Note', sx, sy); sy += 10;
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 30, 30); pdf.setFontSize(7.5);
      const lines = wrapText(pdf, c.text || '', sw);
      pdf.text(lines, sx, sy); sy += lines.length * 10 + 8;
    });
  }
}

function renderFacePage(pdf, page) {
  const PW = A4_W, PH = A4_H;
  const label = FACE_LABELS[page.face] || page.face;
  pdf.setFillColor(35, 35, 55); pdf.rect(0, 0, PW, 36, 'F');
  pdf.setFontSize(10); pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold');
  pdf.text(label, PW / 2, 22, { align: 'center' });
  const aspect = page.faceWidth / page.faceH;
  const maxW = PW - MARGIN * 2, maxH = PH - 36 - MARGIN * 2;
  let imgW = maxW, imgH = imgW / aspect;
  if (imgH > maxH) { imgH = maxH; imgW = imgH * aspect; }
  pdf.addImage(page.imageUrl, 'PNG', (PW - imgW) / 2, 36 + (maxH - imgH) / 2 + MARGIN, imgW, imgH);
}

// ── PDF builder — renders the pages array you pass in, in order ───────────────

/**
 * @param {object[]} pages        - already-filtered page objects to include
 * @param {string}   versionName
 */
export function buildJsPdf(pages, versionName = '') {
  if (!pages?.length) return null;

  const firstOrientation = pages[0].type === 'cover' ? 'landscape' : 'portrait';
  const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: firstOrientation, compress: true });

  pages.forEach((page, idx) => {
    if (idx > 0) {
      pdf.addPage('a4', page.type === 'cover' ? 'landscape' : 'portrait');
    }
    switch (page.type) {
      case 'cover':   renderCoverPage(pdf, page, versionName);   break;
      case 'table':   renderTablePage(pdf, page);                break;
      case 'element': renderElementPage(pdf, page);              break;
      case 'face':    renderFacePage(pdf, page);                 break;
      default: break;
    }
  });

  return pdf;
}

// ── Main hook ─────────────────────────────────────────────────────────────────

export default function usePdfGenerator({ canvas, faceDims, objCommentCounts, draftId, versionId, versionName }) {
  const generatePdfData = useCallback(async () => {
    if (!canvas) throw new Error('Canvas not ready');
    const { faceW, faceH, spineW } = faceDims;
    const totalW = faceW * 2 + spineW;

    // Save canvas state
    const savedDims = { width: canvas.getWidth(), height: canvas.getHeight() };
    const savedVpt  = [...canvas.viewportTransform];
    const savedBg   = canvas.backgroundColor;

    canvas.setDimensions({ width: totalW, height: faceH });
    canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
    if (!canvas.backgroundColor || canvas.backgroundColor === 'transparent') canvas.backgroundColor = '#ffffff';
    canvas.discardActiveObject();
    canvas.requestRenderAll();

    // Full wraparound export
    const coverDataUrl = canvas.toDataURL({ format: 'png', multiplier: 2 });

    // Always export all three faces (for isolated pages + spotlights)
    const faceExports = {
      front: canvas.toDataURL({ format: 'png', multiplier: 2, left: faceW + spineW, top: 0, width: faceW,  height: faceH }),
      back:  canvas.toDataURL({ format: 'png', multiplier: 2, left: 0,              top: 0, width: faceW,  height: faceH }),
      spine: canvas.toDataURL({ format: 'png', multiplier: 2, left: faceW,          top: 0, width: spineW, height: faceH }),
    };

    // Restore canvas before async work
    canvas.setDimensions(savedDims);
    canvas.viewportTransform = savedVpt;
    canvas.backgroundColor   = savedBg;
    canvas.requestRenderAll();

    // Fetch Entire Book comments
    let bookComments = [];
    try {
      const bookRes = await fetch(
        `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/object-comments?collabId=__book__`,
        { credentials: 'omit', headers: getAuthHeaders() },
      );
      const bookData = bookRes.ok ? await bookRes.json() : {};
      bookComments = (bookData.comments || []).filter(c => !c.resolved);
    } catch { bookComments = []; }

    // Collect annotated objects
    const annotatedObjs = canvas.getObjects().filter(o =>
      o._collabId && (objCommentCounts[o._collabId] || 0) > 0 && !SYSTEM_NAMES.has(o.name),
    );

    // Fetch comments in parallel
    const commentResults = await Promise.all(
      annotatedObjs.map(async obj => {
        try {
          const res = await fetch(
            `${API_BASE}/api/drafts/${draftId}/cover/versions/${versionId}/object-comments?collabId=${obj._collabId}`,
            { credentials: 'omit', headers: getAuthHeaders() },
          );
          const data = res.ok ? await res.json() : {};
          return (data.comments || []).filter(c => !c.resolved);
        } catch { return []; }
      }),
    );

    // Generate spotlight images in parallel
    const spotlightUrls = await Promise.all(
      annotatedObjs.map(async (obj, i) => {
        const { face, faceOffsetX, faceWidth } = getFaceForObject(obj, faceW, spineW);
        const br = obj.getBoundingRect?.() ?? { left: obj.left || 0, top: obj.top || 0, width: (obj.width||0)*(obj.scaleX||1), height: (obj.height||0)*(obj.scaleY||1) };
        const bounds = {
          left:   Math.max(0, br.left - faceOffsetX),
          top:    Math.max(0, br.top),
          width:  Math.min(faceWidth, br.width),
          height: Math.min(faceH, br.height),
        };
        try { return await generateSpotlightImage(faceExports[face], bounds, faceWidth, faceH); }
        catch { return faceExports[face] || null; }
      }),
    );

    // Assemble pages — order: cover → table → entire-book → elements → faces
    const tableRows = [];

    // Entire Book row goes first in the table
    if (bookComments.length) {
      tableRows.push({
        label: 'Entire Book',
        faceLabel: '—',
        elementId: 'book',
        instructions: bookComments.map(c => `${c.userName || 'Note'}: ${c.text}`).join(' | '),
      });
    }

    const elementPages = annotatedObjs.map((obj, i) => {
      const { face, faceWidth } = getFaceForObject(obj, faceW, spineW);
      const label     = getObjectLabel(obj, faceDims, annotatedObjs);
      const shortId   = (obj._collabId || '').slice(-6);
      const faceLabel = FACE_LABELS[face] || face;
      const comments  = commentResults[i] || [];

      tableRows.push({
        label, faceLabel, elementId: shortId,
        instructions: comments.length ? comments.map(c => `${c.userName || 'Note'}: ${c.text}`).join(' | ') : 'Normal',
      });

      return { type: 'element', spotlightUrl: spotlightUrls[i], faceImageUrl: faceExports[face], faceLabel, faceWidth, faceH, elementLabel: label, elementId: shortId, comments };
    });

    const bookElementPage = bookComments.length ? {
      type: 'element',
      spotlightUrl: coverDataUrl,
      faceLabel: 'Full Cover',
      faceWidth: totalW,
      faceH,
      elementLabel: 'Entire Book',
      elementId: 'book',
      comments: bookComments,
    } : null;

    const pages = [
      { type: 'cover', imageUrl: coverDataUrl, totalW, faceH, faceW, spineW },
      { type: 'table', rows: tableRows },
      ...(bookElementPage ? [bookElementPage] : []),
      ...elementPages,
      { type: 'face', face: 'front', imageUrl: faceExports.front, faceWidth: faceW,  faceH },
      { type: 'face', face: 'back',  imageUrl: faceExports.back,  faceWidth: faceW,  faceH },
      { type: 'face', face: 'spine', imageUrl: faceExports.spine, faceWidth: spineW, faceH },
    ];

    return { pages, versionName };
  }, [canvas, faceDims, objCommentCounts, draftId, versionId, versionName]);

  return { generatePdfData };
}
